import React, { createContext, useCallback, useContext, useState } from 'react';

type ProductProps = {
  products?: Array<Record<string, string>>;
  sourceProduct: Record<string, string>;
  targetProduct: Record<string, string>;
  updateSourceProduct?: (input: Record<string, string>) => void;
};
type ProductProviderProps = {
  children: React.ReactNode;
  values: ProductProps;
};

const ProductContext = createContext<ProductProps | undefined>(undefined);
const ProductProvider = ({ children, values }: ProductProviderProps) => {
  const [sourceProduct, setSourceProduct] = useState(values.sourceProduct);
  const [targetProduct] = useState(values.targetProduct);

  const updateSourceProduct = useCallback(
    input => {
      setSourceProduct(input);
    },
    [setSourceProduct]
  );

  return (
    <ProductContext.Provider
      value={{
        products: values.products,
        sourceProduct,
        targetProduct,
        updateSourceProduct: values.updateSourceProduct ?? updateSourceProduct
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

const useProducts = () => {
  const context = useContext(ProductContext);

  if (context === undefined) {
    throw new Error('useProducts must be called within a provider.');
  }

  const { products } = context;
  return { products };
};

const useSourceProduct = () => {
  const context = useContext(ProductContext);

  if (context === undefined) {
    throw new Error('useSourceProduct must be called within a provider.');
  }

  const { sourceProduct, updateSourceProduct } = context;
  return { sourceProduct, updateSourceProduct };
};

const useTargetProduct = () => {
  const context = useContext(ProductContext);

  if (context === undefined) {
    throw new Error('useTargetProduct must be called within a provider.');
  }

  const { targetProduct } = context;
  return { targetProduct };
};

export { ProductProvider, useProducts, useSourceProduct, useTargetProduct };
